import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NextSeo } from "next-seo";
import Head from "next/head";
import Link from "next/link";
import { useInfiniteQuery } from "react-query";
import debounce from "lodash/debounce";
import { ChevronRight, Search } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/StaticAccordion";
// Type only: categoryHubs is server code (Mongo, Redis, searchEarners.json).
import type { HubCategory } from "@/utils/categoryHubs";
import { Spinner } from "@jammable/ui-components/Loader";
import {
  JamCardSkeleton,
  JamVoiceCard,
  SectionHeading,
} from "@/components/home/JamCards";
import VoiceSortTabs, {
  VOICE_SORTS,
  VoiceSortKey,
} from "@/components/voices/VoiceSortTabs";

const SITE_URL = "https://www.jammable.com";

// Matches HUB_PAGE_SIZE in utils/categoryHubs.ts. Client pages use the same
// size so page 2 from /api/model starts roughly where SSR stopped.
const PAGE_SIZE = 60;

function titleCase(s: string): string {
  // Badges such as "fictional_character" showed as "Fictional_character AI
  // Voices" in the title and H1.
  return s
    .replace(/_+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Which page numbers the pagination links show: the first and last, two
// either side of the current page, every 10th within 30 of it and every 50th.
// The jumps keep any of ~220 pages a few clicks from page 1 for a crawler,
// where a plain +-2 window would put the middle ~50 pages deep.
function pageNumbers(page: number, lastPage: number): number[] {
  const out = new Set<number>([1, lastPage]);
  for (let n = page - 2; n <= page + 2; n++) out.add(n);
  for (let n = Math.ceil((page - 30) / 10) * 10; n <= page + 30; n += 10)
    out.add(n);
  for (let n = 50; n < lastPage; n += 50) out.add(n);
  return [...out].filter((n) => n >= 1 && n <= lastPage).sort((a, b) => a - b);
}

const PAGER_LINK =
  "tw-flex tw-min-h-[40px] tw-min-w-[40px] tw-items-center tw-justify-center tw-rounded-full tw-px-3 tw-text-sm tw-font-extrabold tw-no-underline";

// Page 1 is the bare URL; ?page=1 redirects there.
const hubPageHref = (hubPath: string, n: number) =>
  n > 1 ? `${hubPath}?page=${n}` : hubPath;

// Server-rendered <a href> links to the hub's pages: the crawl path to every
// voice in the category.
const HubPagination = ({
  page,
  lastPage,
  hubPath,
}: {
  page: number;
  lastPage: number;
  hubPath: string;
}) => {
  const pageHref = (n: number) => hubPageHref(hubPath, n);
  const numbers = pageNumbers(page, lastPage);
  const edge = `${PAGER_LINK} tw-text-jam-purple hover:tw-bg-jam-lav`;
  return (
    <nav
      aria-label="Pages"
      className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1"
    >
      {page > 1 && (
        <>
          <Link href={pageHref(1)} className={edge}>
            First
          </Link>
          <Link href={pageHref(page - 1)} rel="prev" className={edge}>
            Previous
          </Link>
        </>
      )}
      {numbers.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && n - numbers[i - 1] > 1 && (
            <span aria-hidden className="tw-px-1 tw-text-jam-muted">
              …
            </span>
          )}
          {n === page ? (
            <span
              aria-current="page"
              className={`${PAGER_LINK} tw-bg-jam-ink tw-text-white`}
            >
              {n}
            </span>
          ) : (
            <Link
              href={pageHref(n)}
              className={`${PAGER_LINK} tw-text-jam-ink hover:tw-bg-jam-lav`}
            >
              {n}
            </Link>
          )}
        </React.Fragment>
      ))}
      {page < lastPage && (
        <>
          <Link href={pageHref(page + 1)} rel="next" className={edge}>
            Next
          </Link>
          <Link href={pageHref(lastPage)} className={edge}>
            Last
          </Link>
        </>
      )}
    </nav>
  );
};

interface CategoryHubProps {
  category: HubCategory;
  related: HubCategory[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  voices: any[];
  // Page 1 is the trending grid; 2..lastPage list the category by uses.
  page?: number;
  lastPage?: number;
}

const CategoryHub = ({
  category,
  related,
  voices,
  page = 1,
  lastPage = 1,
}: CategoryHubProps) => {
  const label = titleCase(category.badge);
  const hubPath = `/ai-voices/${category.slug}`;
  const pageHref = (n: number) => hubPageHref(hubPath, n);
  // Each page is its own canonical: pages 2+ list different voices, and
  // pointing them at page 1 would tell Google to drop their links.
  const canonical = `${SITE_URL}${pageHref(page)}`;
  const title =
    page > 1
      ? `${label} AI Voices, Page ${page} | Jammable`
      : `${label} AI Voices — Make AI Covers With ${label} Voices | Jammable`;
  const metaDescription =
    page > 1
      ? `Page ${page} of ${lastPage} of ${label} AI voices on Jammable, ranked by uses. Pick any voice to make an AI cover or use the AI voice changer.`
      : `Browse ${category.count.toLocaleString()}+ ${label} AI voices on Jammable. Pick any voice to make an AI cover or use the AI voice changer.`;
  const models = Array.isArray(voices) ? voices : [];
  const filterHref = `/models?filter=${encodeURIComponent(category.badge)}`;

  // Search and sort are client-only: the SSR grid (trending on page 1, by
  // uses on later pages, no search) is what crawlers index.
  const initialSort: VoiceSortKey = page > 1 ? "top" : "hot";
  const [sort, setSort] = useState<VoiceSortKey>(initialSort);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearch(value.trim()), 250),
    [],
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const isDefaultView = sort === initialSort && !search;

  const voicesQuery = useInfiniteQuery(
    // The page is in the key so a client-side hop from ?page=2 to ?page=3
    // seeds from the new SSR voices instead of reusing page 2's cache entry.
    ["hub-voices", category.badge, sort, search, isDefaultView ? page : 0],
    async ({ pageParam = 1 }) => {
      const res = await axios.get("/api/model", {
        params: {
          filter: category.badge,
          search,
          limit: PAGE_SIZE,
          page: pageParam,
          sortOrder: "desc",
          sortBy: VOICE_SORTS[sort].sortBy,
        },
      });
      return res.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.paging?.hasMore ? lastPage.paging.page + 1 : undefined,
      refetchOnWindowFocus: false,
      // Without this the SSR seed is stale on arrival and every hub view
      // re-runs the same 60-voice aggregate from the browser.
      staleTime: 1000 * 60 * 5,
      initialData: isDefaultView
        ? {
            pages: [
              {
                data: models,
                // Page 1 continues in place from /api/model's trending list.
                // Later pages are a different list (submitted voices by
                // uses), so they continue through the page links instead.
                paging: {
                  page: 1,
                  hasMore: page === 1 && models.length >= PAGE_SIZE,
                },
              },
            ],
            pageParams: [1],
          }
        : undefined,
    },
  );

  // /api/model pages are not restricted to submitted voices, so page 2 in
  // place can repeat a voice from the SSR page; one card per voice.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gridVoices: any[] = [];
  const seenIds = new Set<string>();
  for (const p of voicesQuery.data?.pages || []) {
    for (const m of p?.data || []) {
      if (seenIds.has(String(m._id))) continue;
      seenIds.add(String(m._id));
      gridVoices.push(m);
    }
  }
  const nextPageHref = page < lastPage ? pageHref(page + 1) : null;

  const faqs = [
    {
      q: `What are ${label} AI voices?`,
      a: `${label} AI voices are voice models in the ${label.toLowerCase()} category that you can use to make AI covers or change your own voice. Jammable has ${category.count.toLocaleString()}+ of them, with new voices added every day.`,
    },
    {
      q: `How do I use a ${label} AI voice?`,
      a: `Open any ${label} voice, add audio you have the rights to (or record your own), and press generate. Jammable converts the vocals into that voice and returns a finished cover in seconds.`,
    },
    {
      q: `How many ${label} voices are there?`,
      a: `Jammable currently has ${category.count.toLocaleString()}+ ${label} AI voices, and the library grows daily as the community uploads and trains new ones.`,
    },
  ];

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${label} AI Voices`,
    description: metaDescription,
    url: canonical,
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "AI Voices",
        item: `${SITE_URL}/models`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: `${label} AI Voices`,
        item: `${SITE_URL}${hubPath}`,
      },
    ],
  };

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title={title}
        description={metaDescription}
        canonical={canonical}
        // resolveCategorySlug serves any badge with 1+ voices so badge links
        // never 404, but a hub of a handful of voices is a thin page. 25
        // matches the threshold pages/[model].js uses for badge links.
        noindex={category.count < 25}
        openGraph={{
          title,
          description: metaDescription,
          url: canonical,
          type: "website",
          locale: "en_US",
          siteName: "Jammable",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionLd) }}
        />
        {/* The FAQ is page 1's; ~200 copies of it would be duplicate text. */}
        {page === 1 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
        />
      </Head>

      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-3.5 tw-px-4 tw-pb-16 tw-pt-2 md:tw-gap-5 md:tw-px-8 md:tw-pt-4">
        {/* ── Header ─────────────────────────────────────────────── */}
        <nav
          aria-label="Breadcrumb"
          className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-extrabold tw-text-jam-muted md:tw-text-sm"
        >
          <Link
            href="/models"
            className="tw-text-jam-muted tw-no-underline hover:tw-text-jam-purple"
          >
            Voices
          </Link>
          <ChevronRight size={14} strokeWidth={2.6} />
          {page > 1 ? (
            <>
              <Link
                href={hubPath}
                className="tw-text-jam-muted tw-no-underline hover:tw-text-jam-purple"
              >
                {label}
              </Link>
              <ChevronRight size={14} strokeWidth={2.6} />
              <span aria-current="page">Page {page}</span>
            </>
          ) : (
            <span aria-current="page">{label}</span>
          )}
        </nav>

        <h1 className="jam-display tw-m-0 tw-text-[36px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[52px] md:tw-tracking-[-0.045em]">
          {label} AI voices
        </h1>

        <p className="tw-m-0 tw-max-w-2xl tw-text-sm tw-font-semibold tw-leading-normal tw-text-jam-muted md:tw-text-base">
          Browse {category.count.toLocaleString()}+ {label} AI voices. Pick any
          one to make an AI cover, or use it as a voice changer.
        </p>

        <label className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-jam-lav tw-px-4 tw-py-[13px] md:tw-gap-3 md:tw-px-6 md:tw-py-[18px]">
          <Search
            size={18}
            strokeWidth={2.5}
            className="tw-flex-shrink-0 tw-text-jam-muted"
          />
          <input
            aria-label={`Search ${label.toLowerCase()} voices`}
            placeholder={`Search ${label.toLowerCase()} voices`}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSetSearch(e.target.value);
            }}
            className="tw-w-full tw-min-w-0 tw-border-0 tw-bg-transparent tw-text-sm tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted md:tw-text-[17px]"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        {/* ── Voice grid — the internal-link payload ─────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-3.5">
          <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
            <h2 className="jam-display tw-m-0 tw-hidden tw-text-[26px] tw-font-extrabold md:tw-block md:tw-text-[32px]">
              {page > 1
                ? `Most used ${label.toLowerCase()} voices, page ${page} of ${lastPage}`
                : `Popular ${label.toLowerCase()} voices`}
            </h2>
            <VoiceSortTabs value={sort} onChange={setSort} />
          </div>

          {voicesQuery.isLoading ? (
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-6 md:tw-gap-3.5">
              {Array.from({ length: 12 }).map((_, i) => (
                <JamCardSkeleton key={i} className="tw-h-[168px]" />
              ))}
            </div>
          ) : gridVoices.length > 0 ? (
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-6 md:tw-gap-3.5">
              {gridVoices.map((m) => (
                <JamVoiceCard key={m._id} model={m} />
              ))}
              {voicesQuery.isFetchingNextPage &&
                Array.from({ length: 6 }).map((_, i) => (
                  <JamCardSkeleton key={`next-${i}`} className="tw-h-[168px]" />
                ))}
            </div>
          ) : (
            <div className="tw-rounded-[28px] tw-bg-jam-lav tw-px-6 tw-py-10 tw-text-center tw-text-sm tw-font-bold tw-text-jam-muted">
              No {label.toLowerCase()} voices match that search.
            </div>
          )}

          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 tw-py-2">
            {voicesQuery.hasNextPage &&
              (voicesQuery.isFetchingNextPage ? (
                <div className="tw-flex tw-min-h-[44px] tw-items-center tw-px-[22px]">
                  <Spinner size={8} className="tw-border-3" />
                </div>
              ) : nextPageHref ? (
                // A real link to the next page so crawlers can follow it; a
                // plain click still loads more in place.
                <Link
                  href={nextPageHref}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
                      return;
                    e.preventDefault();
                    voicesQuery.fetchNextPage();
                  }}
                  className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-[22px] tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline"
                >
                  Load more voices
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => voicesQuery.fetchNextPage()}
                  className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-[22px] tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink"
                >
                  Load more voices
                </button>
              ))}
            <Link
              href={filterHref}
              className="tw-px-3 tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline"
            >
              Open in the voice library
            </Link>
          </div>

          {lastPage > 1 && (
            <HubPagination page={page} lastPage={lastPage} hubPath={hubPath} />
          )}
        </section>

        {/* ── Related categories ─────────────────────────────────── */}
        {related.length > 0 && (
          <section className="tw-mt-8 tw-flex tw-flex-col tw-gap-4">
            <SectionHeading title="Explore more categories" />
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {related.map((c) => (
                <Link
                  key={c.slug}
                  href={`/ai-voices/${c.slug}`}
                  className="tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-line tw-px-4 tw-py-2 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-border-jam-purple"
                >
                  {c.emoji ? `${c.emoji} ` : ""}
                  {titleCase(c.badge)}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── FAQ ────────────────────────────────────────────────── */}
        {page === 1 && (
          <section className="tw-mt-8 tw-flex tw-flex-col tw-gap-4">
            <SectionHeading title="Frequently asked questions" />
            <Accordion
              type="single"
              collapsible
              className="tw-w-full tw-rounded-[28px] tw-bg-jam-lav tw-px-5 md:tw-px-7"
            >
              {faqs.map((faq, i) => (
                <AccordionItem key={faq.q} value={`faq-${i}`}>
                  <AccordionTrigger className="tw-text-left tw-text-base tw-font-extrabold tw-text-jam-ink">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* ── Keep exploring ─────────────────────────────────────── */}
        <section className="tw-mt-8 tw-flex tw-flex-col tw-gap-4 tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-rounded-[44px] md:tw-p-10">
          <div>
            <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
              Keep exploring
            </h2>
            <p className="tw-m-0 tw-mt-1 tw-text-sm tw-font-semibold tw-text-jam-soft">
              More ways to create with Jammable.
            </p>
          </div>
          <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
            {[
              { label: "AI Cover Generator", href: "/ai-cover" },
              { label: "AI Voice Changer", href: "/ai-voice-changer" },
              { label: "Browse all AI voices", href: "/models" },
              { label: "Train your own voice", href: "/train" },
            ].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-[22px] tw-bg-jam-deep tw-px-[18px] tw-py-4 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline hover:tw-text-jam-lime"
              >
                {link.label}
                <ChevronRight size={18} strokeWidth={2.6} />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default CategoryHub;
